/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const CLAIM_GHOST_DISCRIMINATOR = new Uint8Array([
  149, 107, 35, 233, 252, 101, 148, 225,
]);

export function getClaimGhostDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(CLAIM_GHOST_DISCRIMINATOR);
}

export type ClaimGhostInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountAgentAccount extends string | AccountMeta<string> = string,
  TAccountDidDocument extends string | AccountMeta<string> = string,
  TAccountSasAttestation extends string | AccountMeta<string> = string,
  TAccountClaimer extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAgentAccount extends string
        ? WritableAccount<TAccountAgentAccount>
        : TAccountAgentAccount,
      TAccountDidDocument extends string
        ? WritableAccount<TAccountDidDocument>
        : TAccountDidDocument,
      TAccountSasAttestation extends string
        ? ReadonlyAccount<TAccountSasAttestation>
        : TAccountSasAttestation,
      TAccountClaimer extends string
        ? WritableSignerAccount<TAccountClaimer> &
            AccountSignerMeta<TAccountClaimer>
        : TAccountClaimer,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type ClaimGhostInstructionData = {
  discriminator: ReadonlyUint8Array;
  sasCredential: Address;
  sasSchema: Address;
  ipfsMetadataUri: Option<string>;
  network: string;
};

export type ClaimGhostInstructionDataArgs = {
  sasCredential: Address;
  sasSchema: Address;
  ipfsMetadataUri: OptionOrNullable<string>;
  network: string;
};

export function getClaimGhostInstructionDataEncoder(): Encoder<ClaimGhostInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["sasCredential", getAddressEncoder()],
      ["sasSchema", getAddressEncoder()],
      [
        "ipfsMetadataUri",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      ["network", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
    ]),
    (value) => ({ ...value, discriminator: CLAIM_GHOST_DISCRIMINATOR }),
  );
}

export function getClaimGhostInstructionDataDecoder(): Decoder<ClaimGhostInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["sasCredential", getAddressDecoder()],
    ["sasSchema", getAddressDecoder()],
    [
      "ipfsMetadataUri",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    ["network", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
  ]);
}

export function getClaimGhostInstructionDataCodec(): Codec<
  ClaimGhostInstructionDataArgs,
  ClaimGhostInstructionData
> {
  return combineCodec(
    getClaimGhostInstructionDataEncoder(),
    getClaimGhostInstructionDataDecoder(),
  );
}

export type ClaimGhostInput<
  TAccountAgentAccount extends string = string,
  TAccountDidDocument extends string = string,
  TAccountSasAttestation extends string = string,
  TAccountClaimer extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  agentAccount: Address<TAccountAgentAccount>;
  /**
   * DID document to create for this Ghost
   * Seeds: [b"did_document", agent_account.x402_payment_address.as_ref()]
   */
  didDocument: Address<TAccountDidDocument>;
  /**
   * SAS Attestation proving ownership of x402_payment_address
   * PDA: [b"attestation", sas_credential, sas_schema, x402_payment_address]
   */
  sasAttestation: Address<TAccountSasAttestation>;
  /** The claimer must have created a SAS attestation proving they own x402_payment_address */
  claimer: TransactionSigner<TAccountClaimer>;
  systemProgram?: Address<TAccountSystemProgram>;
  sasCredential: ClaimGhostInstructionDataArgs["sasCredential"];
  sasSchema: ClaimGhostInstructionDataArgs["sasSchema"];
  ipfsMetadataUri: ClaimGhostInstructionDataArgs["ipfsMetadataUri"];
  network: ClaimGhostInstructionDataArgs["network"];
};

export function getClaimGhostInstruction<
  TAccountAgentAccount extends string,
  TAccountDidDocument extends string,
  TAccountSasAttestation extends string,
  TAccountClaimer extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: ClaimGhostInput<
    TAccountAgentAccount,
    TAccountDidDocument,
    TAccountSasAttestation,
    TAccountClaimer,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): ClaimGhostInstruction<
  TProgramAddress,
  TAccountAgentAccount,
  TAccountDidDocument,
  TAccountSasAttestation,
  TAccountClaimer,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agentAccount: { value: input.agentAccount ?? null, isWritable: true },
    didDocument: { value: input.didDocument ?? null, isWritable: true },
    sasAttestation: { value: input.sasAttestation ?? null, isWritable: false },
    claimer: { value: input.claimer ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agentAccount),
      getAccountMeta(accounts.didDocument),
      getAccountMeta(accounts.sasAttestation),
      getAccountMeta(accounts.claimer),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getClaimGhostInstructionDataEncoder().encode(
      args as ClaimGhostInstructionDataArgs,
    ),
    programAddress,
  } as ClaimGhostInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountDidDocument,
    TAccountSasAttestation,
    TAccountClaimer,
    TAccountSystemProgram
  >);
}

export type ParsedClaimGhostInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    agentAccount: TAccountMetas[0];
    /**
     * DID document to create for this Ghost
     * Seeds: [b"did_document", agent_account.x402_payment_address.as_ref()]
     */
    didDocument: TAccountMetas[1];
    /**
     * SAS Attestation proving ownership of x402_payment_address
     * PDA: [b"attestation", sas_credential, sas_schema, x402_payment_address]
     */
    sasAttestation: TAccountMetas[2];
    /** The claimer must have created a SAS attestation proving they own x402_payment_address */
    claimer: TAccountMetas[3];
    systemProgram: TAccountMetas[4];
  };
  data: ClaimGhostInstructionData;
};

export function parseClaimGhostInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedClaimGhostInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 5) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      agentAccount: getNextAccount(),
      didDocument: getNextAccount(),
      sasAttestation: getNextAccount(),
      claimer: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getClaimGhostInstructionDataDecoder().decode(instruction.data),
  };
}
